import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: number; // Pourcentage de changement (positif ou négatif)
  format?: 'number' | 'percentage' | 'duration';
  className?: string;
}

/**
 * Formate une valeur selon son type
 */
function formatValue(value: string | number, format?: 'number' | 'percentage' | 'duration'): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  switch (format) {
    case 'percentage':
      return `${numValue.toFixed(1)}%`;
    case 'duration':
      // Convertir secondes en format lisible
      const minutes = Math.floor(numValue / 60);
      const seconds = Math.floor(numValue % 60);
      return `${minutes}m ${seconds}s`;
    case 'number':
    default:
      return numValue.toLocaleString('fr-FR');
  }
}

/**
 * Carte de métrique avec icône et trend
 */
export function MetricCard({
  title,
  value,
  icon: Icon,
  trend,
  format = 'number',
  className,
}: MetricCardProps) {
  const formattedValue = formatValue(value, format);
  const hasTrend = trend !== undefined && trend !== 0;
  const isPositive = trend && trend > 0;
  const isNegative = trend && trend < 0;

  return (
    <Card className={cn('', className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
        <Icon className="w-4 h-4 text-gray-500" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-gray-900">{formattedValue}</div>
        {hasTrend && (
          <div className="flex items-center gap-1 mt-1">
            {isPositive && (
              <>
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-600">+{Math.abs(trend).toFixed(1)}%</span>
              </>
            )}
            {isNegative && (
              <>
                <TrendingDown className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-600">-{Math.abs(trend).toFixed(1)}%</span>
              </>
            )}
            <span className="text-sm text-gray-500 ml-1">vs période précédente</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
